<template>
  <div 
    class="flex items-center space-x-2 cursor-pointer hover:bg-muted/30 rounded p-1 transition-colors"
    @click="$emit('click')"
  >
    <!-- Color Pip -->
    <div 
      class="w-4 h-4 rounded-full border-2 flex-shrink-0 relative"
      :class="pipClasses"
      :style="pipStyle"
    >
      <!-- On/Off indicator -->
      <div 
        v-if="!light.current_state.on"
        class="absolute inset-0 rounded-full border border-gray-400"
        style="background: repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(128,128,128,0.3) 2px, rgba(128,128,128,0.3) 4px)"
      ></div>
    </div>
    
    <!-- Light Name -->
    <span class="text-sm truncate flex-1" :class="{ 'text-muted-foreground': !light.current_state.on }">
      {{ light.name }}
    </span>
    
    <!-- Brightness indicator -->
    <div 
      v-if="light.current_state.on && brightness"
      class="w-1 h-4 bg-gradient-to-t from-gray-300 to-white rounded-sm flex-shrink-0"
    >
      <div 
        class="bg-yellow-400 rounded-sm transition-all"
        :style="{ height: `${brightness}%` }"
      ></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface LightState {
  on: boolean
  bri?: number
  hue?: number
  sat?: number
  ct?: number
  xy?: [number, number]
  colormode?: 'xy' | 'ct' | 'hs'
}

interface Props {
  light: {
    id: string
    name: string
    current_state: LightState
    light_type: string
    capabilities: Record<string, unknown>
  }
}

const props = defineProps<Props>()

defineEmits<{
  click: []
}>()

const brightness = computed(() => {
  if (!props.light.current_state.on || !props.light.current_state.bri) return 0
  return Math.round((props.light.current_state.bri / 254) * 100)
})

const pipStyle = computed(() => {
  const state = props.light.current_state
  
  if (!state.on) {
    return { backgroundColor: '#6b7280' } // gray-500
  }

  // Handle different color modes
  if (state.colormode === 'xy' && state.xy) {
    // Convert XY to RGB (simplified)
    const [x, y] = state.xy
    const z = 1.0 - x - y
    const Y = (state.bri || 254) / 254
    const X = (Y / y) * x
    const Z = (Y / y) * z
    
    // XYZ to RGB conversion (simplified)
    let r = X * 1.656492 - Y * 0.354851 - Z * 0.255038
    let g = -X * 0.707196 + Y * 1.655397 + Z * 0.036152
    let b = X * 0.051713 - Y * 0.121364 + Z * 1.011530
    
    // Normalize and convert to 0-255
    r = Math.max(0, Math.min(1, r)) * 255
    g = Math.max(0, Math.min(1, g)) * 255
    b = Math.max(0, Math.min(1, b)) * 255
    
    return { backgroundColor: `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})` }
  }
  
  if (state.colormode === 'hs' && state.hue !== undefined && state.sat !== undefined) {
    // Convert HSB to RGB
    const h = (state.hue / 65535) * 360
    const s = (state.sat / 254) * 100
    const v = ((state.bri || 254) / 254) * 100
    
    const hsl = `hsl(${h}, ${s}%, ${Math.max(50, v)}%)`
    return { backgroundColor: hsl }
  }
  
  if (state.colormode === 'ct' && state.ct) {
    // Color temperature to RGB (simplified)
    const temp = 1000000 / state.ct
    let r, g, b
    
    if (temp < 6600) {
      r = 255
      g = Math.max(0, Math.min(255, 99.4708025861 * Math.log(temp) - 161.1195681661))
      b = temp < 1900 ? 0 : Math.max(0, Math.min(255, 138.5177312231 * Math.log(temp - 10) - 305.0447927307))
    } else {
      r = Math.max(0, Math.min(255, 329.698727446 * Math.pow(temp - 60, -0.1332047592)))
      g = Math.max(0, Math.min(255, 288.1221695283 * Math.pow(temp - 60, -0.0755148492)))
      b = 255
    }
    
    return { backgroundColor: `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})` }
  }
  
  // Default warm white
  return { backgroundColor: '#fef3c7' }
})

const pipClasses = computed(() => {
  const baseClasses = 'border-gray-200'
  
  if (!props.light.current_state.on) {
    return `${baseClasses} border-gray-400`
  }
  
  return `${baseClasses} shadow-sm`
})
</script>